'use client';

import React from 'react';
import { cn } from '@/lib/cn';

type FABAlignment = 'left' | 'center' | 'right';
type FABIconPosition = 'left' | 'right';

export interface FloatingActionButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  /** Optional text label */
  label?: string;
  /** SVG icon element rendered inside the button */
  icon?: React.ReactElement;
  /** Handler invoked on click */
  onClick?: () => void;
  /** Aligns the FAB horizontally on the screen */
  alignment?: FABAlignment;
  /** Places the icon on the left or right of the label */
  iconPosition?: FABIconPosition;
  /** Distance in pixels from the bottom of the screen (defaults to 32px on mobile, 24px on desktop) */
  bottomOffset?: number;
  /** Horizontal distance from the screen edge when aligned left/right (defaults to 24px on mobile, 32px on desktop) */
  horizontalOffset?: number;
}

export function FloatingActionButton({
  label,
  icon,
  onClick,
  disabled = false,
  alignment = 'center',
  iconPosition = 'left',
  bottomOffset,
  horizontalOffset,
  className,
  ...props
}: FloatingActionButtonProps) {
  const hasLabel = Boolean(label);
  const hasIcon = Boolean(icon);

  // Position styles based on alignment
  const positionClasses = cn(
    'fixed z-50', // Fixed positioning, high z-index
    alignment === 'center' && 'left-1/2 -translate-x-1/2',
    // For left/right alignment, use inline styles if horizontalOffset is provided
    // Otherwise use default Tailwind classes
    alignment === 'left' && horizontalOffset === undefined && 'left-6 md:left-8',
    alignment === 'right' && horizontalOffset === undefined && 'right-6 md:right-8',
  );

  // Inline styles for custom offsets
  const positionStyle: React.CSSProperties = {};

  if (bottomOffset !== undefined) {
    positionStyle.bottom = `${bottomOffset}px`;
  }

  if (horizontalOffset !== undefined) {
    if (alignment === 'left') {
      positionStyle.left = `${horizontalOffset}px`;
    } else if (alignment === 'right') {
      positionStyle.right = `${horizontalOffset}px`;
    }
  }

  // Padding based on content - matching Figma design
  const paddingClasses = 'px-6 py-4 ';

  // Dimension classes
  const dimensionClasses = hasLabel ? 'min-h-[56px]' : 'min-h-[56px] min-w-[56px]';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        // Base styles
        'inline-flex items-center justify-center',
        'rounded-full',
        'bg-accent hover:bg-accent-hover',
        'text-inverted',
        'shadow-lg',
        'transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-accent',
        'cursor-pointer',
        // Disabled state
        disabled && 'opacity-60 cursor-not-allowed',
        // Dimensions and padding
        dimensionClasses,
        paddingClasses,
        // Position
        positionClasses,
        // Responsive bottom offset - default 32px on mobile, 24px on desktop (when not custom)
        bottomOffset === undefined && 'bottom-20 md:bottom-8',
        className,
      )}
      style={Object.keys(positionStyle).length > 0 ? positionStyle : undefined}
      aria-label={props['aria-label'] || label}
      {...props}
    >
      {hasIcon && iconPosition === 'left' ? (
        <span className={cn('flex items-center justify-center w-4 h-4', hasLabel && 'mr-2')}>
          {icon}
        </span>
      ) : null}

      {hasLabel ? <span className="text-body-2-high whitespace-nowrap">{label}</span> : null}

      {hasIcon && iconPosition === 'right' ? (
        <span className={cn('flex items-center justify-center w-4 h-4', hasLabel && 'ml-2')}>
          {icon}
        </span>
      ) : null}
    </button>
  );
}

export default FloatingActionButton;
